/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {SequentiallyBetWayInitialWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/sequentially/SequentiallyBetWayInitialWPController";
import {Timeline, TimelineLite} from "gsap";
import {PixiesVsPiratesGameConfig} from "../../PixiesVsPiratesGameConfig";
import {PixiesVsPiratesGameModuleConfig} from "../../PixiesVsPiratesGameModuleConfig";
import {PPReelController} from "../../reel/PPReelController";
import {PixiesVsPiratesInitialWinCountUp} from "./PixiesVsPiratesInitialWinCountUp";
import {PixiesVsPiratesInitialWPView} from "./PixiesVsPiratesInitialWPView";
import {PPWaysWonGFXText} from "./PPWaysWonGFXText";
import {PixiesVsPiratesParsedGameData} from "../../server/parseddata/PixiesVsPiratesParsedGameData";

export class PixiesVsPiratesInitialWPController extends SequentiallyBetWayInitialWPController {

    private readonly _symbolDelay:number = 0.1;

    private _gameView:PixiesVsPiratesInitialWPView;
    private _winCountUp:PixiesVsPiratesInitialWinCountUp;
    private _waysWon:PPWaysWonGFXText;

    private _winWaysWon:number = 0;
    private _winNumberTimelines:TimelineLite[];

    constructor(indexes:[number, number]) {
        super(indexes, {
            name : "initialWin",
            hasWinCountUp : true,
            skippable : true
        });

        this._winNumberTimelines = [];

        this._gameView = new PixiesVsPiratesInitialWPView();
        this._winCountUp = new PixiesVsPiratesInitialWinCountUp(PixiesVsPiratesGameConfig.instance.WIN_COUNTUP_BITMAP_STYLE);
        this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.45 - 10);
        StageManager.getLayer(PixiesVsPiratesGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);

        this._waysWon = new PPWaysWonGFXText();
    }

    protected addSingleBetWinAnimationToTimeline(betWayWinData:IBetWayWinData, timeline:Timeline, startTime:number):number {
        const delay:number = super.addSingleBetWinAnimationToTimeline(betWayWinData, timeline, startTime);

        // update betWays won
        this._winWaysWon += betWayWinData.numberOfCombinations;
        const winWaysWon = this._winWaysWon;
        timeline.add(() => this._waysWon.showWinWaysWon(winWaysWon), startTime);

        return delay + 0.5;
    }

    protected addSymbolWinAnimationToTimeline(betWayWinData:IBetWayWinData, timeline:Timeline, startTime:number, symbol:StateReelSymbol, reelId:number, symbolId:number):number {
        const position:[number, number] = Helper.getSymbolPositions(reelId, symbolId, true);
        timeline.add(this._gameView.playWinOnSymbol(symbol, position), startTime);

        if(symbol.stackedSymName!.isStacked) {
            if(symbolId > 6 - (<PPReelController>Reels.getReel(reelId)).reelSize) {
                return this._symbolDelay;
            }
        }
        super.addSymbolWinAnimationToTimeline(betWayWinData, timeline, startTime, symbol, reelId, symbolId);

        // only play on the last reel top symbol.
        if((reelId === betWayWinData.length - 1) && betWayWinData.reelWinPositions[reelId].indexOf(true) === symbolId) {
            // The win number animation will not be removed by abort and stop.
            const winNumberTimeline:TimelineLite = this._gameView.playWinNumbers(betWayWinData, position);
            this._winNumberTimelines.push(winNumberTimeline);
            timeline.add(() => winNumberTimeline.play(), startTime + this._symbolDelay);
            (<TimelineLite>timeline).removeLabel("displayed"); // To remove if that's not the last one.
            (<TimelineLite>timeline).addLabel("displayed", startTime + this._symbolDelay);
            if(!this._gameData!.isBigWin) {
                // delay it until the lash win number start.
                timeline.add(() => {}, `displayed+=${winNumberTimeline.totalDuration()}`);
            }
        }
        return this._symbolDelay;
    }

    protected addWinCountUpToTimeline(timeline:Timeline):void {
        if(this._gameData!.isBigWin) {
            // leave the ways won move down and remove itself.
            return;
        }

        const totalWin:number = this._gameData!.totalWin;
        const from:number = totalWin - this._gameData!.singleWin;
        const duration:number = this._gameData!.currentWinRatioConfig!.duration;

        const countUpTimeline:TimelineLite = new TimelineLite();
        countUpTimeline.add(this._winCountUp.getCountUpAnimation(0, this._gameData!.singleWin, duration, this._gameData!.currentMultiplier));
        countUpTimeline.add(() => SlotGame.winFieldController.showWinField(totalWin));

        timeline.add([
            countUpTimeline,
            () => this._waysWon.shrinkWinWaysWon()
        ], "displayed-=0.2");
    }

    protected startWinPresentation():void {
        super.startWinPresentation();

        // #272
        if (!PixiesVsPiratesGameConfig.instance.WIN_BELOW_STAKE) {
            PixiesVsPiratesGameModuleConfig.soundPlayer.playNormalWinSound(this._gameData!.currentWinRatioConfig!);
        }
    }

    protected stopWinPresentation(isAborted:boolean):void {
        super.stopWinPresentation(isAborted);

        this._gameView.abort();
        this._winCountUp.abort(this._gameData!.singleWin, isAborted, this._gameData!.currentMultiplier, this._gameData!.isBigWin);
        this._waysWon.abort(isAborted, this._gameData!.isBigWin);
        if(!this._gameData!.isBigWin) {
            SlotGame.winFieldController.showWinField();
        }
    }

    public resetFeature():void {
        this._winWaysWon = 0;

        // Only abort/stop win numbers when skip/spinStart/ScreenStart
        while(this._winNumberTimelines.length > 0) {
            const timeline:TimelineLite = this._winNumberTimelines.pop()!;
            timeline.pause();
            timeline.kill();
        }
    }

    protected parseFeatureGameData(data: PixiesVsPiratesParsedGameData): void {
        super.parseFeatureGameData && super.parseFeatureGameData(data);

        // #272
        PixiesVsPiratesGameConfig.instance.WIN_BELOW_STAKE = (SlotGame.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue));
    }
}